import { useEffect, useRef, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { toIsoDate } from '@/core/models/practice.models';
import { NotificationTextMessage } from '@/core/enums/notification';
import {
  TB_DESCRIPTION_MAX_LENGTH,
  TB_REF_MAX_LENGTH,
  TrialBalanceImportMode,
  TrialBalanceType,
  toPeriodOptions,
  type AccountingConnectionStatus,
  type PeriodOption,
  type SaveTrialBalancePayload,
  type TrialBalanceDetail,
  type TrialBalanceDraftHeader,
} from '@/core/models/trial-balance.models';
import { NO_PERIOD_PLACEHOLDER } from '@/core/format/periodLabel';
import { notifier, notifyError, notifySuccess } from '@/core/notify/notify';
import { practiceService } from '@/core/services/practice';
import { trialBalanceService } from '@/core/services/trialBalance';
import { Button } from '@/kit/Button';
import { DateField, type DateValue } from '@/kit/DateField';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Radio, RadioGroup } from '@/kit/Radio';
import { Select } from '@/kit/Select';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { ACCOUNTING_PROVIDERS, connectionAppearsAfterClose, waitForOAuthResult } from './accountingOAuth';
import './TrialBalanceDialog.scss';

export interface TrialBalanceDialogData {
  clientId: string;
}

/**
 * What Create Trial Balance closes with. BUG-037: Manual creates nothing - the confirmed details (`draft`) open an
 * unsaved trial balance, created by its Save. BUG-036: a file (CSV, or exported from QuickBooks, Xero or FreeAgent) is
 * only read - its lines travel with the draft and open in the grid unsaved, for Save to store once they balance.
 * BUG-038: Bookkeeping imports as the old application's Create did, so the trial balance exists already (`created`).
 */
export type TrialBalanceDialogResult = { draft: TrialBalanceDraftHeader } | { created: TrialBalanceDetail };

/** State whose latest value can also be read from an async callback (Angular fields read at response time). */
function useLatest<T>(initial: T) {
  const [value, setValue] = useState(initial);
  const ref = useRef(initial);
  const set = (next: T) => {
    ref.current = next;
    setValue(next);
  };
  return [value, set, ref] as const;
}

const MODES: { id: number; label: string }[] = [
  { id: 0, label: 'Bookkeeping' },
  { id: 1, label: 'CSV' },
  { id: 2, label: 'Manual' },
  { id: 3, label: 'QuickBooks' },
  { id: 4, label: 'Xero' },
  { id: 5, label: 'FreeAgent' },
];

/**
 * BUG-038: the providers whose trial balance is pulled through their own login ("Connect to ..."). The file exported
 * from the provider can still be chosen and read ("Create from File"), as a CSV is.
 */
const PROVIDER_FILE_MODES: readonly number[] = Object.keys(ACCOUNTING_PROVIDERS).map(Number);

const MAX_FILE_BYTES = 2 * 1024 * 1024;

/** BUG-038: why a chosen import file cannot be used (the checks the editor's file picker made), or null. */
function importFileProblem(file: File): string | null {
  if (!/\.(csv|xlsx)$/i.test(file.name)) {
    return 'Choose a .csv or .xlsx file.';
  }
  if (file.size > MAX_FILE_BYTES) {
    return NotificationTextMessage.fileSize2mb;
  }
  return null;
}

/** BUG-038: the new trial balance as createFromBookkeeping sends it (no id; the import gives the lines). */
function toPayload(clientId: string, header: TrialBalanceDraftHeader): SaveTrialBalancePayload {
  return {
    clientId,
    id: null,
    refNo: header.refNo,
    description: header.description,
    typeId: header.typeId,
    importModeId: header.importModeId,
    accountingPeriodId: header.accountingPeriodId || null,
    fromDate: header.fromDate,
    toDate: header.toDate,
    lines: null,
  };
}

/**
 * Angular TrialBalanceDialogComponent: "Create Trial Balance". BUG-037: the Period and Mode of Import are always the
 * user's choice (never preset) and required, the Ref. No. is at most 10 characters and the Description one line of at
 * most 50 - the rules the server applies to every caller. BUG-038: the Mode of Import is one choice (radio buttons, as
 * the old application) and decides what Create does: Manual saves nothing (the editor's Save creates the trial
 * balance); CSV reads the selected file into the editor's grid unsaved (BUG-036: its Save stores it once it balances);
 * QuickBooks, Xero and FreeAgent replace Create with "Connect to ..." (the provider's login in a popup, then the period's
 * trial balance opens in the grid unsaved, accounts the chart lacks flagged) and keep "Create from File" for the file
 * exported from the provider; Bookkeeping pulls the balances from Capium Bookkeeping and creates the trial balance.
 */
export function TrialBalanceDialog({
  data,
  dialogRef,
}: DialogComponentProps<TrialBalanceDialogData, TrialBalanceDialogResult>) {
  const [refNo, setRefNo, refNoRef] = useLatest('');
  const [description, setDescription] = useState('');
  const [typeId, setTypeId] = useState<number>(TrialBalanceType.Annual);
  const [accountingPeriodId, setAccountingPeriodId, accountingPeriodIdRef] = useLatest<string | null>(null);
  const [periodFrom, setPeriodFrom] = useState<DateValue>(null);
  const [periodTo, setPeriodTo] = useState<DateValue>(null);
  // BUG-037: no Mode of Import until the user picks one.
  const [importModeId, setImportModeId] = useState<number | null>(null);
  const [periods, setPeriods] = useState<PeriodOption[]>([]);
  // BUG-038: the file a CSV or provider trial balance is read from.
  const [file, setFile] = useState<File | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  /** True after a Create was refused: the empty mandatory fields are outlined red. */
  const [showErrors, setShowErrors] = useState(false);
  const creatingRef = useRef(false);
  // BUG-038: what the selected provider's Connect can do (configured? connected already?), and what it is waiting for.
  const [integrationStatus, setIntegrationStatus] = useState<AccountingConnectionStatus | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  // Aborted when the dialog goes away, so a sign-in popup left open does not act on a dialog that is gone.
  const connectAbort = useRef<AbortController | null>(null);

  const onPeriodChange = (period: PeriodOption | null | undefined) => {
    setAccountingPeriodId(period?.id ?? null);
    if (period) {
      setPeriodFrom(period.periodFrom);
      setPeriodTo(period.periodTo);
    }
  };

  useEffect(() => {
    if (!data?.clientId) {
      notifyError('Select a client from Accounts first.');
      return;
    }
    let cancelled = false;
    Promise.all([
      trialBalanceService.getNextRefNo(data.clientId),
      practiceService.getClientPeriods(data.clientId),
    ]).then(
      ([nextRefNo, clientPeriods]) => {
        if (cancelled) return;
        if (!refNoRef.current) {
          setRefNo(nextRefNo);
        }
        // cap3-h: a new trial balance cannot go on a locked, finalised (5) or submitted (6) period.
        const options = toPeriodOptions(
          (clientPeriods.periods ?? []).filter((p) => !p.isLocked && !(Number(p.statusId) >= 5)),
        );
        // BUG-037: the period is the user's choice; none is preselected.
        setPeriods(options);
      },
      (error) => {
        if (!cancelled) notifyError(error);
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ngOnInit: once
  }, []);

  /**
   * task-115: an annual trial balance reports on the chosen accounting period, so it shows the
   * period picker and takes its dates from it. A management-only one covers a period the user
   * decides, so it shows the dates and not the picker.
   */
  const isAnnual = Number(typeId) === TrialBalanceType.Annual;

  const onTypeChange = (next: number) => {
    setTypeId(next);
    // Going back to Annual re-derives the dates, so a management period someone typed cannot
    // survive as the reporting period of an annual trial balance.
    if (Number(next) === TrialBalanceType.Annual) {
      const period = periods.find((p) => p.id === accountingPeriodIdRef.current);
      if (period) {
        setPeriodFrom(period.periodFrom);
        setPeriodTo(period.periodTo);
      }
    } else if (!periodFrom && !periodTo) {
      // Oct-10 #108 (AP Tasks 3348): as in 3.0, a management-only trial balance starts with the period dates filled in (the
      // chosen period's, else the latest open one's); the user can change them.
      const period =
        periods.find((p) => p.id === accountingPeriodIdRef.current) ??
        [...periods].sort((a, b) => String(b.periodTo).localeCompare(String(a.periodTo)))[0];
      if (period) {
        setPeriodFrom(period.periodFrom);
        setPeriodTo(period.periodTo);
      }
    }
  };

  /** BUG-038: the provider (Xero, QuickBooks, FreeAgent) the Mode of Import selects, or undefined for the other modes. */
  const provider = importModeId !== null ? ACCOUNTING_PROVIDERS[importModeId] : undefined;
  const providerLabel = provider?.name;

  useEffect(
    () => () => {
      connectAbort.current?.abort();
    },
    [],
  );

  // Whether Connect can work is shown as soon as a provider is chosen; no answer (an error) leaves Connect to find out.
  const clientIdForStatus = data?.clientId;
  const providerId = provider?.id;
  useEffect(() => {
    if (!providerId || !clientIdForStatus) {
      return;
    }
    let cancelled = false;
    trialBalanceService.getIntegrationStatus(providerId, clientIdForStatus).then(
      (status) => {
        if (!cancelled) setIntegrationStatus(status);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [providerId, clientIdForStatus]);
  // A status read for another provider (the user changed mode meanwhile) is not this provider's.
  const integration = integrationStatus && integrationStatus.provider === providerId ? integrationStatus : null;

  const onDownloadTemplate = () => {
    trialBalanceService.downloadTemplate().catch((error) => notifyError(error));
  };

  const onFileChange = (input: HTMLInputElement) => {
    const chosen = input.files && input.files.length > 0 ? input.files[0] : null;
    // Reset so choosing the same file again fires change again.
    input.value = '';
    if (!chosen) {
      return;
    }
    const problem = importFileProblem(chosen);
    if (problem) {
      notifyError(problem);
      return;
    }
    setFile(chosen);
  };

  /** The confirmed details, or null when one is missing or not valid (its message is shown). */
  const confirmedHeader = (): TrialBalanceDraftHeader | null => {
    setShowErrors(true);
    const ref = refNo?.trim() ?? '';
    if (!ref) {
      notifyError('Enter the trial balance reference.');
      return null;
    }
    if (ref.length > TB_REF_MAX_LENGTH) {
      notifyError(`The reference can be at most ${TB_REF_MAX_LENGTH} characters, for example TB-1.`);
      return null;
    }
    const text = description?.trim() ?? '';
    if (text.length > TB_DESCRIPTION_MAX_LENGTH || /[\r\n]/.test(text)) {
      notifyError(`The description is one line of at most ${TB_DESCRIPTION_MAX_LENGTH} characters.`);
      return null;
    }
    if (isAnnual && !accountingPeriodId) {
      notifyError('Select the accounting period for this trial balance.');
      return null;
    }
    // task-115: the dates are the user's own on a management-only trial balance, so they are
    // checked here; on an annual one they come from the accounting period and cannot be blank.
    if (!isAnnual) {
      if (!periodFrom || !periodTo) {
        notifyError('Enter the period this management trial balance covers.');
        return null;
      }
      if (toIsoDate(periodTo)! < toIsoDate(periodFrom)!) {
        notifyError('The period cannot end before it starts.');
        return null;
      }
    }
    if (importModeId === null) {
      notifyError('Select the Mode of Import.');
      return null;
    }
    const period = periods.find((p) => p.id === accountingPeriodId);
    return {
      refNo: ref,
      description: text || null,
      typeId: Number(typeId),
      importModeId,
      accountingPeriodId,
      periodLabel: period?.label ?? '',
      fromDate: toIsoDate(periodFrom),
      toDate: toIsoDate(periodTo),
    };
  };

  /**
   * APT340 (reopened by the client: "restrict client in the first step itself under +Add Trial balance"): a client cannot have two trial
   * balances with exactly the same From and To dates, so it is asked here, before any line is entered or any file read, and the server's
   * message (it names the trial balance that has the dates) is shown with the dialog left open. Overlapping periods are allowed. Save applies
   * the same rule again on the server.
   */
  const datesAreFree = async (header: TrialBalanceDraftHeader, clientId: string) => {
    try {
      await trialBalanceService.checkDates(clientId, {
        accountingPeriodId: header.accountingPeriodId,
        fromDate: header.fromDate,
        toDate: header.toDate,
        // APT373: Annual and Management only trial balances are compared separately.
        typeId: header.typeId,
      });
      return true;
    } catch (error) {
      notifyError(error);
      return false;
    }
  };

  /** BUG-038: creates the trial balance on the server (Bookkeeping) and closes with it; a refusal is shown. */
  const createWith = async (
    create: () => Promise<TrialBalanceDetail>,
    successMessage: (created: TrialBalanceDetail) => string,
  ) => {
    creatingRef.current = true;
    setIsCreating(true);
    try {
      const created = await create();
      notifySuccess(successMessage(created));
      dialogRef.close({ created });
    } catch (error) {
      notifyError(error);
    } finally {
      creatingRef.current = false;
      setIsCreating(false);
    }
  };

  /**
   * BUG-036: reads the file's lines and closes with them on the draft - nothing is saved; the editor opens them in the
   * grid unsaved and its Save stores them once they balance. A file that cannot be read is shown and the dialog stays.
   */
  const readInto = async (header: TrialBalanceDraftHeader, chosen: File, clientId: string) => {
    creatingRef.current = true;
    setIsCreating(true);
    try {
      // BUG-065/067/068: read for the client, so old-app account codes follow its client type and chart.
      const lines = await trialBalanceService.readFile(chosen, clientId || undefined);
      notifySuccess(`${lines.length} line(s) read from ${chosen.name}. Save the trial balance to keep them.`);
      // A provider file can use codes the client's chart does not have (e.g. Xero 200): saving is not blocked (they
      // report as unclassified), but the user is told once and each such line carries its note in the grid.
      const outside = lines.filter((line) => line.legacyCodeStatus === 'notInChart').length;
      if (outside > 0) {
        notifier.warn(
          'Warning!',
          outside === 1
            ? '1 line uses an account that is not in the chart of accounts. Pick an account or add it to the chart.'
            : `${outside} lines use accounts that are not in the chart of accounts. Pick an account or add them to the chart.`,
        );
      }
      dialogRef.close({ draft: { ...header, lines } });
    } catch (error) {
      notifyError(error);
    } finally {
      creatingRef.current = false;
      setIsCreating(false);
    }
  };

  /** BUG-038: reads the connected provider's trial balance for the period and opens it in the editor unsaved. */
  const importFromProvider = async (header: TrialBalanceDraftHeader, clientId: string) => {
    if (!provider) return;
    setProgress(`Reading the trial balance from ${provider.name}...`);
    const lines = await trialBalanceService.importFromIntegration(provider.id, toPayload(clientId, header));
    const toCheck = lines.filter((line) => line.legacyCodeStatus === 'check').length;
    notifySuccess(
      `${lines.length} line(s) imported from ${provider.name}.` +
        (toCheck > 0 ? ` ${toCheck} line(s) need an account chosen.` : '') +
        ' Save the trial balance to keep them.',
    );
    dialogRef.close({ draft: { ...header, lines } });
  };

  /**
   * BUG-038: "Connect to Xero / QuickBooks / FreeAgent". The API says where to sign in; the provider's login opens in a
   * popup and returns to /oauth/callback, which stores the connection; the period's trial balance is then read. A
   * provider the firm has not set up, a refused or abandoned sign-in and an expired request each say so.
   */
  const onConnect = async (reconnect = false) => {
    if (creatingRef.current || !provider) {
      return;
    }
    const header = confirmedHeader();
    if (!header) {
      return;
    }
    if (integration && !integration.configured) {
      notifyError(integration.message ?? `${provider.name} is not configured for this firm - ask your administrator.`);
      return;
    }
    const clientId = data?.clientId ?? '';
    // The popup is opened now, inside the click, so the browser lets it through; it is pointed at the provider later.
    const popup = window.open('', 'capium-accounting-oauth', 'popup,width=640,height=760');
    creatingRef.current = true;
    setIsCreating(true);
    try {
      // APT340: the dates are checked before the provider is contacted (the popup was opened first, inside the click).
      if (!(await datesAreFree(header, clientId))) {
        popup?.close();
        return;
      }
      // If already connected, clicking Connect reconnects so the provider login opens (e.g. 85993/85994).
      const shouldReconnect = reconnect || Boolean(integration?.connected);
      const connect = await trialBalanceService.connectIntegration(provider.id, clientId, shouldReconnect);
      if (!connect.configured) {
        popup?.close();
        notifyError(connect.message ?? `${provider.name} is not configured for this firm - ask your administrator.`);
        return;
      }
      if (!connect.connected || shouldReconnect) {
        if (!popup || !connect.authorizeUrl) {
          popup?.close();
          notifyError(
            `The ${provider.name} sign-in window could not be opened. Allow pop-ups for this site and try again.`,
          );
          return;
        }
        popup.location.href = connect.authorizeUrl;
        setProgress(`Waiting for you to sign in to ${provider.name}...`);
        connectAbort.current = new AbortController();
        const result = await waitForOAuthResult(popup, connectAbort.current.signal);
        if (connectAbort.current.signal.aborted) {
          return;
        }
        if (!result) {
          // Closed, or cut off from this window: a connection that now exists (for a client that had none) is this sign-in's.
          setProgress(`Checking whether the ${provider.name} sign-in finished...`);
          const finished = await connectionAppearsAfterClose(
            async () => (await trialBalanceService.getIntegrationStatus(provider.id, clientId)).connected,
            connectAbort.current.signal,
          );
          if (!finished) {
            notifyError(`The ${provider.name} sign-in window was closed before it finished. Nothing was imported.`);
            return;
          }
        } else if (!result.ok) {
          notifyError(result.message ?? `The ${provider.name} sign-in did not finish. Nothing was imported.`);
          return;
        }
      } else {
        popup?.close();
      }
      await importFromProvider(header, clientId);
    } catch (error) {
      popup?.close();
      notifyError(error);
    } finally {
      creatingRef.current = false;
      setIsCreating(false);
      setProgress(null);
    }
  };

  const onCreate = async () => {
    if (creatingRef.current) {
      return;
    }
    const header = confirmedHeader();
    if (!header) {
      return;
    }
    const clientId = data?.clientId ?? '';
    // APT340: a trial balance whose From and To dates another one already has is refused here, at the first step.
    creatingRef.current = true;
    setIsCreating(true);
    let free = false;
    try {
      free = await datesAreFree(header, clientId);
    } finally {
      creatingRef.current = false;
      setIsCreating(false);
    }
    if (!free) {
      return;
    }
    // BUG-038: a provider's trial balance is imported from the file exported from it, as a CSV is.
    if (header.importModeId === TrialBalanceImportMode.Csv || PROVIDER_FILE_MODES.includes(header.importModeId)) {
      if (!file) {
        notifyError('Select the file to import.');
        return;
      }
      void readInto(header, file, clientId);
      return;
    }
    if (header.importModeId === TrialBalanceImportMode.Bookkeeping) {
      void createWith(
        () => trialBalanceService.createFromBookkeeping(toPayload(clientId, header)),
        (created) => `${created.lines.length} line(s) imported from Capium Bookkeeping`,
      );
      return;
    }
    // BUG-037: Manual - nothing is saved until the trial balance's own Save.
    dialogRef.close({ draft: header });
  };

  return (
    <div className="mat-dialog-wrapper tr-tb-dialog">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>Create Trial Balance</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div>
              <div className="form-field">
                <p>
                  Ref. No.<span className="text-red">*</span>
                </p>
                <InputField
                  fieldClassName="w-387"
                  charMode="alphanumeric"
                  maxLength={TB_REF_MAX_LENGTH}
                  name="refNo"
                  invalid={showErrors && !refNo?.trim()}
                  value={refNo}
                  onChange={(event) => setRefNo(event.target.value)}
                />
              </div>
              <div className="form-field">
                <p>Description</p>
                <InputField
                  fieldClassName="w-387"
                  maxLength={TB_DESCRIPTION_MAX_LENGTH}
                  name="description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>
              <div className="form-field mb-0">
                <p>Type</p>
                <RadioGroup name="typeId" value={typeId} onChange={(value) => onTypeChange(value as number)}>
                  <div className="d-flex align-center gap-15">
                    <Radio value={0} hostClassName="example-radio-button">
                      Annual
                    </Radio>
                    <Radio value={1} hostClassName="example-radio-button">
                      Management only
                    </Radio>
                  </div>
                </RadioGroup>
              </div>
              {/* task-115: Annual reports on the accounting period, so it picks one and the
                  dates follow. Management-only covers a period of the user's choosing, so it
                  takes the dates directly and has no accounting period to pick. */}
              {isAnnual ? (
                <div className="form-field">
                  <p>
                    Period<span className="text-red">*</span>
                  </p>
                  <Select<PeriodOption>
                    className={`w-387${showErrors && !accountingPeriodId ? ' ng-invalid ng-touched' : ''}`}
                    name="accountingPeriodId"
                    placeholder={NO_PERIOD_PLACEHOLDER}
                    items={periods}
                    bindLabel="label"
                    bindValue="id"
                    value={accountingPeriodId || null}
                    onChange={(_, item) => onPeriodChange(item)}
                  />
                </div>
              ) : (
                <div className="d-flex gap-30">
                  <div className="form-field">
                    <p>
                      From<span className="text-red">*</span>
                    </p>
                    <DateField
                      fieldClassName="w-180"
                      name="periodFrom"
                      invalid={showErrors && !periodFrom}
                      value={periodFrom}
                      onChange={(value: Dayjs | null) => setPeriodFrom(value)}
                    />
                  </div>
                  <div className="form-field">
                    <p>
                      To<span className="text-red">*</span>
                    </p>
                    <DateField
                      fieldClassName="w-180"
                      name="periodTo"
                      invalid={showErrors && !periodTo}
                      value={periodTo}
                      onChange={(value: Dayjs | null) => setPeriodTo(value)}
                    />
                  </div>
                </div>
              )}
              <div className="form-field mb-0">
                <p>
                  Mode of Import<span className="text-red">*</span>
                </p>
                {/* BUG-038: one choice, as the old application's radio buttons. */}
                <RadioGroup
                  name="importModeId"
                  value={importModeId}
                  onChange={(value) => setImportModeId(value as number)}
                >
                  <div className="mode-rdo">
                    {MODES.map((mode) => (
                      <Radio key={mode.id} value={mode.id} hostClassName="example-radio-button">
                        {mode.label}
                      </Radio>
                    ))}
                  </div>
                </RadioGroup>
              </div>
              {/* BUG-038: the CSV template and file belong to creating the trial balance (item 123), not its editor. */}
              {(importModeId === TrialBalanceImportMode.Csv || providerLabel) && (
                <div className="csv-wrap">
                  {importModeId === TrialBalanceImportMode.Csv && (
                    <div className="mb-10 pb-10 mt-1 hr-bottom">
                      <a className="link text-underline-none d-flex align-items-center" onClick={onDownloadTemplate}>
                        <Icon name="file_download" className="font-size-18" />
                        Download Template File
                      </a>
                    </div>
                  )}
                  {providerLabel && (
                    <>
                      {/* BUG-038: the provider's own login, as the old application's "Connect to ...". */}
                      <p
                        className={`font-size-12 mt-1 tb-provider-status ${integration && !integration.configured ? 'text-red' : 'text-gray'}`}
                        role="status"
                      >
                        {integration && !integration.configured
                          ? (integration.message ??
                            `${providerLabel} is not configured for this firm - ask your administrator.`)
                          : integration?.connected
                            ? `Connected to ${integration.connectedTo ?? providerLabel}. Connect reads this period's trial balance from ${providerLabel}.`
                            : `Select Connect to ${providerLabel} to sign in and read this period's trial balance.`}
                      </p>
                      {integration?.connected && (
                        <p className="font-size-12 mt-1">
                          <a className="link" onClick={() => void onConnect(true)}>
                            {`Connect a different ${providerLabel} account`}
                          </a>
                        </p>
                      )}
                      <p className="font-size-12 text-gray mt-1 tb-provider-instructions">
                        {`Or export the trial balance for this period from ${providerLabel} as a CSV or Excel (.xlsx) file, select it below and choose Create from File. Its lines open in the trial balance for you to check and save.`}
                      </p>
                    </>
                  )}
                  <div className="form-field attached">
                    <p>
                      {providerLabel ? `Select File exported from ${providerLabel}` : 'Select File'}
                      <span className="text-red">*</span>
                    </p>
                    <div className="attachFile">
                      <Icon name="cloud_upload" outlined />
                      <input
                        type="file"
                        autoComplete="off"
                        id="tb-import-file"
                        name="importFile"
                        accept=".csv,.xlsx"
                        onChange={(event) => onFileChange(event.target)}
                      />
                      <label htmlFor="tb-import-file">
                        <strong>Choose a file </strong>
                        <span className="dragndrop"> or Drag it here to upload</span>.
                        <div className="mt-5">(Max file size - 2 MB)</div>
                      </label>
                    </div>
                    <div className="mt-5 tb-import-file-name">{file ? file.name : 'No file chosen'}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            {/* BUG-038: a provider mode replaces Create with "Connect to ..."; the file it exported can still be created from. */}
            {providerLabel && (
              <Button
                className="action-button primary-button"
                type="button"
                busy={isCreating}
                onClick={() => void onConnect()}
              >
                {`Connect to ${providerLabel}`}
              </Button>
            )}
            <Button
              className={`action-button ${providerLabel ? 'secondary-button' : 'primary-button'}`}
              type="button"
              busy={isCreating}
              onClick={() => void onCreate()}
            >
              {providerLabel ? 'Create from File' : 'Create'}
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
          {progress && (
            <p className="font-size-12 text-gray mt-1 tb-provider-progress" role="status">
              {progress}
            </p>
          )}
        </form>
      </DialogContent>
    </div>
  );
}
